/* eslint-disable */
/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

/*import url and gateway methods */
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

//import images
import { assetImages } from "constants";

//import current date
import { getCurrentDateInString } from "helper/getcurrentdatestring";

//import membership options from json
import MembershipStatusOption from "data/Prod/MembershipStatus.json";

/**
 * MemberDetailsPopup Component
 * Displays and manages course member details in an offcanvas popup
 * Allows updating member role and membership status
 */
const MemberDetailsPopup = ({
  roleList,
  memberId, // Renamed from partnerId for clarity in this component
  setMemberId, // Renamed from setPartnerId
  afterPopupClose,
  courseId, // Renamed from eventId
}) => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation(); //for translation

  // ========== STATE MANAGEMENT ==========

  /** Course member unique identifier */
  const [courseMemberId, setCourseMemeberId] = useState(null);

  /** Member profile information */
  const [memberImage, setmemberImage] = useState("");
  const [memberName, setmemberName] = useState("");
  const [memberEmail, setmemberEmail] = useState("");
  const [memberRole, setmemberRole] = useState("");

  /** Role selection state management */
  const [roleValue, setRoleValue] = useState(null);
  const [roleData, setRoleData] = useState(null);

  /** Membership status state management */
  const [memberStatusValue, setmemberStatusValue] = useState(null);
  const [memberStatusData, setmemberStatusData] = useState(null);
  const [courseMemberStatus, setCourseMemberStatus] = useState("0");

  /** Loading state for save operations */
  const [isSaving, setIsSaving] = useState(false);

  // ========== API FUNCTIONS ==========

  /**
   * Fetches detailed information about the course member
   * Populates form fields with existing data for editing
   */
  const getMemberDetails = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_COURSE_MEMBER_DETAILS + // Keep this specific to course members
        `/${memberId}`;

      const response = await getData(requestUrl);
      console.log(response);

      if (response.status) {
        setCourseMemeberId(response.data.coursememberid);
        setmemberImage(response.data.image); // Use 'image' for member
        setmemberName(response.data.name); // Use 'name' for member
        setmemberEmail(response.data.email);
        setmemberRole(response.data.rolestring);
        roleSelectionHandler(response.data.roledata);
        MembershipStatusOption.map((membership) => {
          if (response.data.moderatorstatus == membership.value) {
            memberShipStatusSelectionHandler(membership);
          }
        });
        setCourseMemberStatus(response.data.memberstatus);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Updates course member membership information including role and status
   * Handles API call and popup closure on success
   */
  const updateMemberHandler = async () => {
    try {
      setIsSaving(true);

      let memberData = {
        role: roleData,
        moderatorstatus: memberStatusData,
        joiningdate: memberStatusData == "1" ? getCurrentDateInString() : "",
      };

      let requestUrl =
        url.API_BASE_URL +
        url.API_UPDATE_COURSE_MEMBER + // Keep this specific to course members
        `/${memberId}?courseid=${courseId}&coursememberid=${courseMemberId}&coursememberstatus=${courseMemberStatus}`;

      const response = await putData(requestUrl, memberData);

      console.log(response);

      setIsSaving(false);

      if (response.status) {
        //hide member modal
        let offCanvasPopup = document.querySelector("#offcanvasMemberDetails"); // Keep original ID
        let offCanvas = bootstrap.Offcanvas.getInstance(offCanvasPopup);
        offCanvas.hide();

        closePopupHandler();
        afterPopupClose();
      }
    } catch (error) {
      console.log(error.message);
      setIsSaving(false);
    }
  };

  // ========== EVENT HANDLERS ==========

  /**
   * Handles role selection from dropdown
   * Updates both display value and data for API submission
   */
  const roleSelectionHandler = (val) => {
    setRoleValue(val);
    setRoleData(val?.value);
  };

  /**
   * Handles membership status selection
   * Updates both display value and data for API submission
   */
  const memberShipStatusSelectionHandler = (val) => {
    setmemberStatusValue(val);
    setmemberStatusData(val?.value);
  };

  /**
   * Resets all form states when popup is closed
   * Ensures clean state for next usage
   */
  const closePopupHandler = () => {
    setMemberId(null);
    setCourseMemeberId(null);
    setmemberImage("");
    setmemberName("");
    setmemberEmail("");
    setmemberRole("");
    setRoleValue(null);
    setRoleData(null);
    setmemberStatusValue(null);
    setmemberStatusData(null);
  };

  // ========== CUSTOM STYLES ==========

  /** Custom styles for react-select components */
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: "#ced4da",
      "&:hover": {
        borderColor: "#ced4da",
      },
      minHeight: "40px",
      boxShadow: "none",
      padding: "3px",
      borderRadius: "6px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#6c757d",
    }),
    input: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
  };

  // ========== LIFECYCLE EFFECTS ==========

  /**
   * Fetches member details when memberId is provided
   * Triggers on component mount or memberId change
   */
  useEffect(() => {
    if (memberId) {
      getMemberDetails();
    }
  }, [memberId]);

  return (
    <div
      className="offcanvas md offcanvas-end bg-white border-0"
      tabIndex="-1"
      id="offcanvasMemberDetails" // Keep original ID
      aria-labelledby="offcanvasMemberDetailsLabel"
    >
      {/* ========== POPUP HEADER ========== */}
      <div className="offcanvas-header p-4 pb-0 border-bottom border-gray-300">
        <h3
          className="offcanvas-title fw-bold text-primary"
          id="offcanvasMemberDetailsLabel"
        >
          {t("Member Details")}
        </h3>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-none m-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={closePopupHandler}
        ></button>
      </div>

      {/* ========== POPUP BODY ========== */}
      <div className="offcanvas-body d-flex flex-column p-4">
        {/* ========== MEMBER PROFILE SECTION ========== */}
        <div className="profile-section mb-4 p-3 bg-light rounded-10">
          <div className="d-flex align-items-center gap-3">
            <div
              className="avatar rounded-circle overflow-hidden border border-gray-300"
              style={{ width: "50px", height: "50px" }}
            >
              <img
                src={
                  memberImage == ""
                    ? assetImages.defaultUser
                    : url.SERVER_URL + memberImage
                }
                alt="Member Avatar"
                className="w-100 h-100 object-fit-cover object-center"
              />
            </div>
            <div className="flex-grow-1">
              <h5 className="fw-semibold mb-1 text-dark">{memberName}</h5>
              <p className="text-muted mb-0 fs-sm">{memberEmail}</p>
            </div>
          </div>
        </div>

        {/* ========== CURRENT INFORMATION TABLE ========== */}
        <div className="current-info-section mb-4">
          <h6 className="fw-semibold mb-3 text-secondary">
            {t("Current Information")}
          </h6>
          <div className="table-responsive">
            <table className="table table-borderless fs-sm">
              <tbody>
                <tr className="border-bottom border-gray-200">
                  <td className="bg-transparent py-2 text-muted">
                    {t("Current Role")}
                  </td>
                  <td className="bg-transparent py-2 fw-semibold text-dark">
                    {memberRole}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ========== EDIT FORM SECTION ========== */}
        <div className="edit-form-section flex-grow-1">
          <h6 className="fw-semibold mb-3 text-secondary border-top border-gray-300 pt-3">
            {t("Update Member Details")}
          </h6>

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Role Selection Field */}
            <div className="form-group mb-4">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Assign Role")} <span className="text-danger">*</span>
              </label>
              <Select
                styles={customSelectStyles}
                placeholder={t(
                  "Select a role for this member (e.g., Instructor, Student)"
                )}
                isClearable
                options={roleList}
                value={roleValue}
                onChange={roleSelectionHandler}
              />
            </div>

            {/* Membership Status Field */}
            <div className="form-group mb-4">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Membership Status")} <span className="text-danger">*</span>
              </label>
              <Select
                styles={customSelectStyles}
                placeholder={t(
                  "Choose membership status (e.g., Active, Pending, Inactive)"
                )}
                isClearable
                options={MembershipStatusOption}
                value={memberStatusValue}
                onChange={memberShipStatusSelectionHandler}
              />
            </div>

            {/* Action Buttons */}
            <div className="action-buttons-section mt-4 pt-3 border-top border-gray-300">
              <div className="d-flex gap-3 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  data-bs-dismiss="offcanvas"
                  onClick={closePopupHandler}
                  disabled={isSaving}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={updateMemberHandler}
                  disabled={isSaving || !roleData || !memberStatusData}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Updating...")}
                    </>
                  ) : (
                    t("Update Member")
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberDetailsPopup;
